import {
    createRouter,
    createWebHistory,
    NavigationGuardNext,
    RouteLocationNormalized,
    RouteLocationNormalizedLoaded,
    Router,
    RouteRecordRaw
} from 'vue-router';

import * as tmi from "@/constants";

// Services
import routeManager from "@/router/route-manager";

// Plugins
import pinia from "@/plugins/PiniaPlugin";

// Constants
import {
    courseKey,
    discordKey,
    discordRegistrationKey,
    etfListingKey,
    majorIndexBreadthKey,
    notFoundKey,
    nyseBpiKey,
    portfolioBuilderKey,
    positionSizeCalculatorKey,
    relativeStrengthMatrixKey,
    sectorAlertsKey,
    tradeAlertSearchKey
} from "@/types/products/product-definitions";

// Pinia Stores
import { useApplicationStore } from "@/stores/Application";
import { usePermissionsStore } from "@/stores/Permissions";

// import { consoleLoggerFactory, getDefaultLoggerOptions, type ILogger, LogLevels } from "@/types/console-logger";
// const logger: ILogger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );
// Statically loaded components - need to be available immediately upon loading
import Authorization from '@/pages/Authorization.vue';
import Registration from '@/pages/Registration.vue';
import Forbidden403 from '@/pages/Forbidden403.vue';
import Home from '@/pages/Home.vue';

import CachedComponentWrapper from "@/pages/CachedComponentWrapper.vue";
import { sectorDetailsKey } from "@/plugins/SectorToolsPlugin";
import { storeToRefs } from "pinia";
import { useAuthStore } from "@/stores/Auth";
import BonusReportsSearch from '@/components/search/BonusReportSearch.vue';
import Courses from '@/components/courses/Courses.vue';
import WebinarsSearch from '@/components/search/WebinarSearch.vue';

import { useCoursesStore } from "@/stores/Courses";
import { NotFound } from "@tmi/types/api/responses";

export const myArticlesKey: string = 'my-articles';
export const myWebinarsKey: string = 'my-webinars';

const About = () => import( '@/pages/About.vue' );

// --- PRODUCTS
const Products = () => import( '@/pages/Product.vue' );

const AllBPIs = () => import( '@/pages/Tools/AllBPIs.vue' );
const Article = () => import( '@/pages/Article.vue' );
const TradeAlert = () => import( '@/pages/TradeAlert.vue' );
const Author = () => import( '@/pages/Author.vue' );

// Components used within the Product Pages
export const SectorsAsync = () => import( '@/components/sectors/SectorsList.vue' );
// export const SrsCalculatorAsync = () => import( '@/components/calculators/StockReplacementStrategyCalculator.vue' );

const CalculationHelp = () => import( '@/pages/CalculationHelp.vue' );

const Login = () => import( '@/components/Login.vue' );
const NewPassword = () => import( '@/pages/ResetPassword/NewPassword.vue' );
const Profile = () => import( '@/pages/Profile.vue' );
const MyProfile = () => import( '@/components/my/MyProfile.vue' );
const ResetPassword = () => import( '@/pages/ResetPassword/index.vue' );
const SimpleContentfulPage = () => import( '@/pages/SimpleContentfulPage.vue' );
const SectorEdge = () => import( '@/pages/SectorEdge.vue' );
const SecurityDetails = () => import( '@/components/securities/SecurityDetails.vue' );
const Webinar = () => import( '@/pages/Webinar.vue' );

const ArticleSearch = () => import( "@/components/search/ArticleSearch.vue" );

const AppToolsPage = () => import( '@/pages/Support/AppTools.vue' );

const routes: RouteRecordRaw[] = [
    // -------- Default landing page & alias "/" --------
    {
        path: tmi.HomePath,
        name: tmi.HomeName,
        alias: tmi.DashboardPath,
        meta: { public: true, indexEligible: true },
        component: Home
    },
    // -------- Unauthenticated/Public free content routes (media/sector-edge/sectoredge) --------
    {
        path: tmi.SectorEdgePath,
        name: tmi.SectorEdgeSlug,
        meta: { public: true, indexEligible: true },
        component: SectorEdge
    },
    // -------- Redirector for /sector-edge => /sectoredge --------
    {
        path: tmi.SectorDashEdgePath,
        redirect: tmi.SectorEdgePath
    },
    // -------- Redirector for /media => /sectoredge --------
    {
        path: tmi.MediaPath,
        redirect: tmi.SectorEdgePath
    },
    // -------- Unauthenticated/Public WTF Email Confirmation bullshit route --------
    {
        path: tmi.TestimonialPath,
        meta: { public: true },
        props: {
            slug: tmi.TestimonialSlug,
        },
        component: SimpleContentfulPage
    },
    // -------- Unauthenticated/Public WTF stand-alone testimonial bullshit route --------
    {
        path: tmi.EmailConfirmationPath,
        meta: { public: true },
        props: {
            slug: tmi.EmailConfirmationSlug,
        },
        component: SimpleContentfulPage
    },
    // -------- Unauthenticated/Public informational routes --------
    {
        path: tmi.AboutUsPath,
        name: tmi.AboutUsName,
        meta: { public: true, indexEligible: true },
        component: About
    },
    {
        path: tmi.ContactUsPath,
        name: tmi.ContactUsDisplayText,
        meta: { public: true, indexEligible: true },
        props: {
            slug: tmi.ContactUsSlug,
        },
        component: SimpleContentfulPage
    },
    {
        path: tmi.PrivacyPolicyPath,
        name: tmi.PrivacyPolicySlug,
        meta: { public: true, indexEligible: true },
        props: {
            slug: tmi.PrivacyPolicySlug,
        },
        component: SimpleContentfulPage
    },
    {
        path: tmi.RefundPolicyPath,
        name: tmi.RefundPolicySlug,
        meta: { public: true, indexEligible: true },
        props: {
            slug: tmi.RefundPolicySlug
        },
        component: SimpleContentfulPage
    },
    {
        path: tmi.TermsOfUsePolicyPath,
        name: tmi.TermsOfUsePolicySlug,
        meta: { public: true, indexEligible: true },
        props: {
            slug: tmi.TermsOfUsePolicySlug
        },
        component: SimpleContentfulPage
    },
    {
        path: tmi.SmsTextPolicyPath,
        name: tmi.SmsTextPolicySlug,
        meta: { public: true, indexEligible: true },
        props: {
            slug: tmi.SmsTextPolicySlug
        },
        component: SimpleContentfulPage
    },
    {
        path: tmi.WhitelistInstructionsPath,
        name: tmi.WhitelistInstructionsSlug,
        meta: { public: true, indexEligible: true },
        props: {
            slug: tmi.WhitelistInstructionsSlug
        },
        component: SimpleContentfulPage
    },
    // -------- Authentication routes --------
    {
        path: tmi.SignInPath,
        meta: { public: true, indexEligible: true },
        component: Authorization,
        children: [
            {
                path: tmi.SignInPath,
                name: tmi.SignInName,
                meta: { public: true, indexEligible: true },
                components: {
                    loginSection: Login
                }
            },
            {
                path: tmi.SignInAsUserPath,
                name: tmi.SignInAsUserName,
                meta: { public: true, indexEligible: true },
                components: {
                    loginSection: Login
                }
            }
        ]
    },
    {
        path: tmi.SignUpPath,
        name: tmi.SignUpName,
        meta: { public: true, indexEligible: true },
        component: Registration,
    },
    {
        path: tmi.ResetPasswordPath,
        name: tmi.ResetPasswordName,
        meta: { public: true },
        component: ResetPassword
    },
    {
        path: tmi.NewPasswordPath,
        name: tmi.NewPasswordName,
        meta: { public: true },
        component: NewPassword
    },
    // -------- Product routing --------
    {
        path: tmi.ProductsPath,
        name: tmi.ProductsName,
        alias: tmi.ProductsAdvancedAliasPath,
        component: Products,
        meta: { public: true, indexEligible: true }
    },
    // -------- Tools routes --------
    {
        path: tmi.NyseBpiPath,
        name: tmi.NyseBpiName,
        meta: {
            public: true,
            category: nyseBpiKey
        },
        component: CachedComponentWrapper
    },
    // -------- Content-based routes --------
    {
        path: tmi.ArticleSearchPath,
        name: tmi.ArticleSearchName,
        meta: { public: true },
        component: ArticleSearch
    },
    {
        path: tmi.BonusReportsSearchPath,
        name: tmi.BonusReportsSearchName,
        meta: {
            public: false,
            permission: tmi.BonusReportsPermissionKey
        },
        component: BonusReportsSearch
    },
    {
        path: tmi.AuthorBioPath,
        name: tmi.AuthorBioName,
        meta: { public: true, indexEligible: true },
        component: Author
    },
    // -------- Alert-based routes --------
    {
        path: tmi.TradeAlertsPath,
        name: tmi.TradeAlertsName,
        alias: tmi.LearningTradeAlertsAliasPath,
        meta: {
            public: false,
            permission: tmi.ProductTradeAlertsPermissionKey,
            category: tradeAlertSearchKey
        },
        component: CachedComponentWrapper
    },
    {
        path: tmi.TradeAlertPath,
        name: tmi.TradeAlertName,
        alias: tmi.LearningTradeAlertAliasPath,
        meta: {
            public: false,
            permission: tmi.ProductTradeAlertsPermissionKey
        },
        component: TradeAlert
    },
    // -------- Article-based routes --------
    {
        path: tmi.ArticlePath,
        name: tmi.ArticleName,
        meta: { public: true, indexEligible: true },
        component: Article
    },
    // -------- Webinar-based routes --------
    {
        path: tmi.WebinarSearchPath,
        name: tmi.WebinarsName,
        meta: {
            public: false,
            permission: tmi.WebinarsPermissionsKey
        },
        component: WebinarsSearch
    },
    { path: tmi.WebinarAllAliasPath, redirect: tmi.WebinarSearchPath },
    { path: tmi.WebinarCompletedAliasPath, redirect: tmi.WebinarSearchPath },
    { path: tmi.WebinarMainAliasPath, redirect: tmi.WebinarSearchPath },
    { path: tmi.WebinarUpcomingAliasPath, redirect: tmi.WebinarSearchPath },
    {
        path: tmi.WebinarPath,
        name: tmi.WebinarName,
        meta: { public: false, permission: tmi.WebinarsPermissionsKey },
        component: Webinar
    },
    // --------  Lesson-based routes --------
    {
        path: tmi.CoursesPath,
        name: tmi.CoursesName,
        meta: {
            public: false,
            permission: tmi.CoursePermissionsKey
        },
        beforeEnter: () => {
            useCoursesStore( pinia ).initializeAsync().then();
        },
        component: Courses
    },
    {
        path: tmi.CoursePath,
        name: tmi.CourseName,
        meta: {
            public: false,
            permission: tmi.CoursePermissionsKey,
            category: courseKey
        },
        beforeEnter: ( to ) => {
            const coursesStore = useCoursesStore( pinia );
            coursesStore.initializeAsync().then();

            if ( typeof to.params.courseSlug !== 'string' ) {
                // TODO: update the UI to display the lack of authorization
                // reject the navigation
                throw new NotFound();
            }

            coursesStore.setSelectCourseByCourseSlug( `${ to.params.courseSlug }` );
        },
        component: CachedComponentWrapper
    },
    {
        path: tmi.ForbiddenPath,
        name: tmi.ForbiddenName,
        meta: { public: true },
        component: Forbidden403
    },
    {
        path: tmi.SectorTreePath,
        name: tmi.SectorTreeSlug,
        meta: { public: false, permission: tmi.SectorsPermissionKey },
        props: {
            slug: tmi.SectorTreeSlug,
        },
        component: SimpleContentfulPage
    },
    {
        path: tmi.SectorsPath,
        name: tmi.SectorsName,
        meta: {
            public: false,
            permission: tmi.SectorsPermissionKey
        },
        component: SectorsAsync
    },
    {
        path: tmi.EtfListingPath,
        name: tmi.EtfListingName,
        meta: {
            public: false,
            permission: tmi.EtfListingPermissionsKey,
            category: etfListingKey
        },
        component: CachedComponentWrapper,
    },
    {
        path: tmi.RelativeStrengthMatrixPath,
        name: tmi.RelativeStrengthMatrixName,
        alias: tmi.MatrixHubRelativeStrengthPath,
        meta: {
            public: false,
            permission: tmi.RelativeStrengthMatrixPermissionKey,
            category: relativeStrengthMatrixKey
        },

        component: CachedComponentWrapper
    },
    {
        path: tmi.SectorDetailsPath,
        name: tmi.SectorDetailsName,
        meta: {
            public: false,
            permission: tmi.SectorsPermissionKey,
            category: sectorDetailsKey
        },
        component: SectorsAsync,
    },
    {
        path: tmi.SecurityDetailsPath,
        meta: {
            public: false,
            permission: tmi.StockPermissionsKey
        },
        component: SecurityDetails
    },
    {
        path: tmi.EtfDetailsAliasPath,
        redirect: to => {
            return { path: `${ tmi.SecurityDetailsBasePath }/${ to.params.ticker }` }
        }
    },
    {
        path: tmi.StockDetailsAliasPath,
        redirect: to => {
            return { path: `${ tmi.SecurityDetailsBasePath }/${ to.params.ticker }` }
        }
    },
    /* Private routes */
    {
        path: tmi.ProfilePath,
        meta: { public: false, permission: tmi.ProfilePermissionKey },
        component: Profile,
        children: [
            {
                path: '',
                name: tmi.ProfileName,
                props: { activeTab: tmi.Articles },
                meta: { public: false, permission: tmi.ProfilePermissionKey },
                component: MyProfile
            },
            {
                path: tmi.MyLibraryPath,
                name: tmi.MyLibraryName,
                props: {
                    activeTab: tmi.Articles
                },
                meta: {
                    public: false,
                    permission: tmi.ProfilePermissionKey,
                    category: myArticlesKey
                },
                component: CachedComponentWrapper
            },
            {
                path: tmi.MyWebinarsPath,
                name: tmi.MyWebinarsName,
                props: {
                    activeTab: tmi.Webinars
                },
                meta: {
                    public: false,
                    permission: tmi.ProfilePermissionKey,
                    category: myWebinarsKey
                },
                component: CachedComponentWrapper
            }
        ]
    },
    {
        path: tmi.MajorIndexBreadthPath,
        name: tmi.MajorIndexBreadthName,
        meta: {
            public: false,
            permission: tmi.MajorIndexBreadthPermissionsKey,
            category: majorIndexBreadthKey
        },
        component: CachedComponentWrapper
    },
    {
        path: '/tools/options-calculator/calculation-help',
        name: 'calculation-help',
        meta: { public: true },
        component: CalculationHelp
    },
    {
        path: routeManager.spreadCalculatorPath(),
        name: tmi.SpreadCalculatorKey,
        meta: {
            public: false,
            permission: tmi.SpreadCalculatorsPermissionKey,
            category: tmi.SpreadCalculatorKey
        },
        component: CachedComponentWrapper
    },
    {
        path: '/tools/position-size-calculator',
        name: 'PositionSizeCalculator',
        meta: {
            public: false,
            permission: tmi.PositionSizeCalculatorPermissionKey,
            category: positionSizeCalculatorKey
        },
        component: CachedComponentWrapper
    },
    {
        path: tmi.SectorBullishPercentIndicesPath,
        name: tmi.SectorBullishPercentIndicesName,
        alias: tmi.AllSectorBPIsPath,
        meta: {
            public: false,
            permission: tmi.AllSectorBullishPercentIndexesPermissionKey
        },
        component: AllBPIs
    },
    {
        path: tmi.PortfolioBuilderPath,
        alias: tmi.PortfoliosPath,
        name: tmi.PortfolioBuilderName,
        meta: {
            public: false,
            permission: tmi.PortfolioBuilderPermissionKey,
            category: portfolioBuilderKey
        },
        component: CachedComponentWrapper
    },
    {
        path: tmi.SectorAlertsPath,
        name: tmi.SectorAlertsName,
        alias: tmi.AlertsAliasPath,
        meta: {
            public: false,
            permission: tmi.SectorAlertsPermissionKey,
            category: sectorAlertsKey
        },
        component: CachedComponentWrapper
    },
    // {
    //     path: tmi.TradeOfTheDayPath,
    //     name: tmi.TradeOfTheDayName,
    //     meta: {
    //         public: false,
    //         permission: tmi.StockPermissionsKey,
    //         // permission: tmi.TradeOfTheDayPermissionKey,
    //         category: tradeOfTheDayKey
    //     },
    //     component: CachedComponentWrapper
    // },
    {
        path: tmi.AppToolsPath,
        name: tmi.AppToolsName,
        alias: tmi.AppToolsPath,
        meta: {
            public: true
        },
        component: AppToolsPage
    },
    // -------- Discord OAuth routes --------
    {
        path: tmi.DiscordPath,
        name: tmi.DiscordName,
        meta: {
            public: false,
            permission: tmi.DashboardPermissionsKey,
            category: discordKey
        },
        component: CachedComponentWrapper
    },
    {
        path: tmi.DiscordCallbackPath,
        name: tmi.DiscordCallbackName,
        meta: {
            public: false,
            permission: tmi.DiscordPermissionsKey,
            category: discordRegistrationKey
        },
        component: CachedComponentWrapper
    },
    {
        path: "/:catchAll(.*)",
        name: notFoundKey,
        meta: {
            public: true,
            category: notFoundKey
        },
        component: CachedComponentWrapper
    }
]

const router: Router = createRouter( {
    history: createWebHistory( import.meta.env.BASE_URL ),
    scrollBehavior( _: RouteLocationNormalized, __: RouteLocationNormalizedLoaded, savedPosition ) {
        return savedPosition || new Promise( ( resolve ) => {
            setTimeout( () => resolve( { top: 0 } ), 300 )
        } );
    },
    routes
} );

/*
---------------------------------------------
The Full Navigation Resolution Flow
---------------------------------------------
  * Navigation triggered.
     1. Call beforeRouteLeave guards in deactivated components.
     2. Call global beforeEach guards.
     3. Call beforeRouteUpdate guards in reused components.
     4. Call beforeEnter in route configs.
     5. Resolve async route components.
     6. Call beforeRouteEnter in activated components.
     7. Call global beforeResolve guards.
     8. Navigation is confirmed.
     9. Call global afterEach hooks.
    10. DOM updates triggered.
    11. Call callbacks passed to next in beforeRouteEnter guards with instantiated instances
*/

// Step #2 in the overall flow
router.beforeEach( async ( to: RouteLocationNormalized, _: RouteLocationNormalizedLoaded, next: NavigationGuardNext ): Promise<void> => {
    const { refreshVersion } = useApplicationStore( pinia );
    await refreshVersion();

    // Removed client faulting check - navigation is not blocked by API errors
    next();
} );

// Step #7 in the lifecycle flow above
router.beforeResolve( async ( to: RouteLocationNormalized, _: RouteLocationNormalizedLoaded, next: NavigationGuardNext ): Promise<void> => {
    const { isAuthenticated } = storeToRefs( useAuthStore( pinia ) );
    if ( !isAuthenticated.value ) {
        localStorage.removeItem( 'fav_art' );
        localStorage.removeItem( 'fav_sec' );
        localStorage.removeItem( 'fav_web' );
        localStorage.removeItem( 'model-portfolios' );
        localStorage.removeItem( 'my-portfolios' );
        localStorage.removeItem( 'nextLinkAfterLogin' );
        localStorage.removeItem( 'permissions' );
        localStorage.removeItem( 'user' );
    }

    const routeRequiresAuthentication: boolean = !to.meta?.public;
    const routeRequiresSpecificPermissions: boolean = !!( to.meta?.permission );

    // The selected route requires authorization
    if ( routeRequiresAuthentication || routeRequiresSpecificPermissions ) {
        // The following series of predicates will fire in the situation where there exists a valid authentication token
        // in localstorage, but the permissions have not been loaded/re-loaded into memory. This can happen in multiple scenarios:
        //      1) The user manually edits the url in the browser
        //      2) The user refreshes the browser
        //      3) A new tab/window is opened during the time window when the authentication token is still valid

        // Divert the user to the sign-in page b/c they are requesting a route that requires authentication and they are not authenticated
        if ( !isAuthenticated.value ) {
            sessionStorage.setItem( 'nextLinkAfterLogin', to?.path ?? routeManager.homePath() );
            next( { name: tmi.SignInName } );
            return;
        }

        const permissionsStore = usePermissionsStore( pinia );
        if ( !permissionsStore.isInitialized ) {
            // Load any permissions available
            permissionsStore.initializeSynchronously();
            // Attempt to pre-initialize all auth-required stores,
            // but only in the case where permissions had to be loaded/reloaded
            const { initializeAsync } = useApplicationStore( pinia );
            await initializeAsync();
        }

        // User is authenticated, validate that they have the correct permissions
        const metaPermission: string = `${ to.meta?.permission ?? '' }`;
        const isAuthorizedForRoute: boolean = permissionsStore.hasFeatureAccess( metaPermission );

        // user is NOT authorized for route
        if ( !isAuthorizedForRoute ) {
            // Marking the current status for the user as "Forbidden" or 403
            next( { path: routeManager.forbiddenPath() } );
            return;
        }
    }

    // The selected route is not protected
    next();
} );

export default router;
